import { Box, Divider, Grid, InputAdornment, Modal, Stack, Typography, styled, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { Loader } from "Components/_Shared/Loader";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  getGetAssetsOverviewQueryKey,
  getListFinancialAssetItemsQueryKey,
  getListImmovableAssetItemsQueryKey,
  getListLiabilityItemsQueryKey,
  getListMovableAssetItemsQueryKey,
  useGetAssetsOverview,
  useListFinancialAssetItems,
  useListImmovableAssetItems,
  useListLiabilityItems,
  useListMovableAssetItems,
  useSetFinancialAssetsEntryMode,
  useSetImmovableAssetsEntryMode,
  useSetLiabilitiesEntryMode,
  useSetMovableAssetsEntryMode,
  useUpdateAggregatedFinancialAssets,
  useUpdateAggregatedImmovableAssets,
  useUpdateAggregatedLiabilities,
  useUpdateAggregatedMovableAssets,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  type FinancialAssetItem,
  type ImmovableAssetItem,
  type LiabilityItem,
  type MovableAssetItem,
} from "Infrastructure/ApiZfp/Schema";
import { useResponsive } from "Infrastructure/Hooks/useResponsive";
import { Radius, Spacing, TABLET_MAX_WIDTH } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { calculateAssetsCombinedValue } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { AssetSummary } from "./AssetSummary";
import { FinancialAssetDrawer } from "./FinancialAssetDrawer";
import { FinancialAssetTile } from "./FinancialAssetTile";
import { ImmovableAssetDrawer } from "./ImmovableAssetDrawer";
import { ImmovableAssetTile } from "./ImmovableAssetTile";
import { LiabilityDrawer } from "./LiabilityDrawer";
import { LiabilityTile } from "./LiabilityTile";
import { MovableAssetDrawer } from "./MovableAssetDrawer";
import { MovableAssetTile } from "./MovableAssetTile";

type EntryMode = "aggregate" | "individual";

const StyledWrapper = styled(Box)`
  width: ${(props) => props.theme.spacing(150)};
  border-radius: ${(props) => props.theme.spacing(Radius.XXXL)};
  padding: ${(props) => props.theme.spacing(Spacing.XXL)};
  background: ${(props) => props.theme.colors.white};
`;

export const AssetsPane: FC = () => {
  const theme = useTheme();
  const isTablet = useResponsive(TABLET_MAX_WIDTH);
  const queryClient = useQueryClient();
  const { t } = useResource();
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const [immovableAssetDrawerOpen, setImmovableAssetDrawerOpen] = useState(false);
  const [immovableAssetData, setImmovableAssetData] = useState<ImmovableAssetItem>();
  const [movableAssetDrawerOpen, setMovableAssetDrawerOpen] = useState(false);
  const [movableAssetData, setMovableAssetData] = useState<MovableAssetItem>();
  const [financialAssetDrawerOpen, setFinancialAssetDrawerOpen] = useState(false);
  const [financialAssetData, setFinancialAssetData] = useState<FinancialAssetItem>();
  const [liabilityDrawerOpen, setLiabilityDrawerOpen] = useState(false);
  const [liabilityData, setLiabilityData] = useState<LiabilityItem>();

  const invalidateAssetsOverview = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
  };

  const invalidateImmovableAssets = () => {
    invalidateAssetsOverview();
    queryClient.invalidateQueries({ queryKey: getListImmovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const invalidateMovableAssets = () => {
    invalidateAssetsOverview();
    queryClient.invalidateQueries({ queryKey: getListMovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const invalidateFinancialAssets = () => {
    invalidateAssetsOverview();
    queryClient.invalidateQueries({ queryKey: getListFinancialAssetItemsQueryKey(analysisId, versionId) });
  };

  const invalidateLiabilities = () => {
    invalidateAssetsOverview();
    queryClient.invalidateQueries({ queryKey: getListLiabilityItemsQueryKey(analysisId, versionId) });
  };

  const { data: assets, isLoading: isAssetsOverviewLoading } = useGetAssetsOverview(analysisId, versionId);
  const { data: immovableAssetItems, isLoading: isImmovableAssetItemsLoading } = useListImmovableAssetItems(analysisId, versionId);
  const { data: movableAssetItems, isLoading: isMovableAssetItemsLoading } = useListMovableAssetItems(analysisId, versionId);
  const { data: financialAssetItems, isLoading: isFinancialAssetItemsLoading } = useListFinancialAssetItems(analysisId, versionId);
  const { data: liabilityItems, isLoading: isLiabilityItemsLoading } = useListLiabilityItems(analysisId, versionId);

  const isAssetsLoading =
    isAssetsOverviewLoading ||
    isImmovableAssetItemsLoading ||
    isMovableAssetItemsLoading ||
    isFinancialAssetItemsLoading ||
    isLiabilityItemsLoading;

  const { mutate: setImmovableAssetsEntryMode } = useSetImmovableAssetsEntryMode();
  const { mutate: setMovableAssetsEntryMode } = useSetMovableAssetsEntryMode();
  const { mutate: setFinancialAssetsEntryMode } = useSetFinancialAssetsEntryMode();
  const { mutate: setLiabilitiesEntryMode } = useSetLiabilitiesEntryMode();

  const { mutate: updateImmovableAssetAggregate } = useUpdateAggregatedImmovableAssets();
  const { mutate: updateMovableAssetAggregate } = useUpdateAggregatedMovableAssets();
  const { mutate: updateFinancialAssetAggregate } = useUpdateAggregatedFinancialAssets();
  const { mutate: updateLiabilitiesAggregate } = useUpdateAggregatedLiabilities();

  const {
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const defaultValues = {
      immovableAssetsEntryType: assets?.immovableAssetsEntryMode ?? "individual",
      movableAssetsEntryType: assets?.movableAssetsEntryMode ?? "individual",
      financialAssetsEntryType: assets?.financialAssetsEntryMode ?? "individual",
      liabilitiesEntryType: assets?.liabilitiesEntryMode ?? "individual",
      aggregateImmovableAssetsAmount: assets?.aggregatedImmovableAssets?.aggregatedAssetsAmount?.value.toString() ?? "",
      aggregateMovableAssetsAmount: assets?.aggregatedMovableAssets?.aggregatedAssetsAmount?.value.toString() ?? "",
      aggregateFinancialAssetsAmount: assets?.aggregatedFinancialAssets?.aggregatedAssetsAmount?.value.toString() ?? "",
      aggregateLiabilitiesAmount: assets?.aggregatedLiabilities?.aggregatedAssetsAmount?.value.toString() ?? "",
      isImmovableAggregateUsableForGoals: assets?.aggregatedImmovableAssets.isUsableForGoals,
      isMovableAggregateUsableForGoals: assets?.aggregatedMovableAssets.isUsableForGoals,
      isFinancialAggregateUsableForGoals: assets?.aggregatedFinancialAssets.isUsableForGoals,
      hasAllocatedFinancialAssets: assets?.hasAllocatedFinancialAssets ?? false,
      hasAllocatedImmovableAssets: assets?.hasAllocatedImmovableAssets ?? false,
      hasAllocatedMovableAssets: assets?.hasAllocatedMovableAssets ?? false,
    };

    reset(defaultValues);
  }, [assets, reset]);

  const immovableAssetsEntryType = watch("immovableAssetsEntryType");
  const movableAssetsEntryType = watch("movableAssetsEntryType");
  const financialAssetsEntryType = watch("financialAssetsEntryType");
  const liabilitiesEntryType = watch("liabilitiesEntryType");

  const aggregateImmovableAssetsAmount = watch("aggregateImmovableAssetsAmount");
  const aggregateMovableAssetsAmount = watch("aggregateMovableAssetsAmount");
  const aggregateFinancialAssetsAmount = watch("aggregateFinancialAssetsAmount");
  const aggregateLiabilitiesAmount = watch("aggregateLiabilitiesAmount");

  const hasAllocatedFinancialAssets = watch("hasAllocatedFinancialAssets");
  const hasAllocatedImmovableAssets = watch("hasAllocatedImmovableAssets");
  const hasAllocatedMovableAssets = watch("hasAllocatedMovableAssets");

  const isImmovableAggregateUsableForGoals = watch("isImmovableAggregateUsableForGoals");
  const isMovableAggregateUsableForGoals = watch("isMovableAggregateUsableForGoals");
  const isFinancialAggregateUsableForGoals = watch("isFinancialAggregateUsableForGoals");

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationMutation, setConfirmationMutation] = useState<(() => void) | null>(null);

  const openConfirmationModal = (mutation: () => void) => {
    setConfirmationModalOpen(true);
    setConfirmationMutation(() => mutation);
  };

  const handleConfirmationSubmit = () => {
    confirmationMutation?.();
    setConfirmationModalOpen(false);
    setConfirmationMutation(null);
  };

  const handleConfirmationCancel = () => {
    setConfirmationModalOpen(false);
    setConfirmationMutation(null);
    reset();
  };

  const changeImmovableAmountEntryType = (amountEntryModel: EntryMode) => {
    const mutate = () =>
      setImmovableAssetsEntryMode(
        { analysisId, versionId, mode: amountEntryModel },
        {
          onSuccess: invalidateImmovableAssets,
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
        },
      );

    if (!hasAllocatedImmovableAssets) {
      mutate();
      return;
    }
    openConfirmationModal(mutate);
  };

  const changeMovableAmountEntryType = (amountEntryModel: EntryMode) => {
    const mutate = () =>
      setMovableAssetsEntryMode(
        { analysisId, versionId, mode: amountEntryModel },
        {
          onSuccess: invalidateMovableAssets,
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
        },
      );

    if (!hasAllocatedMovableAssets) {
      mutate();
      return;
    }
    openConfirmationModal(mutate);
  };

  const changeFinancialAmountEntryType = (amountEntryModel: EntryMode) => {
    const mutate = () =>
      setFinancialAssetsEntryMode(
        { analysisId, versionId, mode: amountEntryModel },
        {
          onSuccess: invalidateFinancialAssets,
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
        },
      );

    if (!hasAllocatedFinancialAssets) {
      mutate();
      return;
    }
    openConfirmationModal(mutate);
  };

  const onAggregateImmovableAssetAmountChange = (isUsableForGoals: boolean) => {
    try {
      const mutate = () =>
        updateImmovableAssetAggregate(
          {
            analysisId,
            versionId,
            data: {
              aggregatedAssetsAmount: {
                value: Number(aggregateImmovableAssetsAmount),
                currencyCode: "CZK",
                frequency: AmountFrequency.one_time,
              },
              isUsableForGoals,
            },
          },
          {
            onSuccess: () => {
              invalidateImmovableAssets();
              toast.success(t(Resources.AnalysisDetail.Assets.Immovable.Edited));
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error)),
          },
        );

      if (hasAllocatedImmovableAssets && !isUsableForGoals) {
        openConfirmationModal(mutate);
        return;
      }
      mutate();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Immovable.Error));
    }
  };

  const onAggregateMovableAssetAmountChange = (isUsableForGoals: boolean) => {
    try {
      const mutate = () =>
        updateMovableAssetAggregate(
          {
            analysisId,
            versionId,
            data: {
              aggregatedAssetsAmount: {
                value: Number(aggregateMovableAssetsAmount),
                currencyCode: "CZK",
                frequency: AmountFrequency.one_time,
              },
              isUsableForGoals,
            },
          },
          {
            onSuccess: () => {
              invalidateMovableAssets();
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Edited));
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );

      if (hasAllocatedMovableAssets && !isUsableForGoals) {
        openConfirmationModal(mutate);
        return;
      }
      mutate();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const onAggregateFinancialAssetAmountChange = (isUsableForGoals: boolean) => {
    try {
      const mutate = () =>
        updateFinancialAssetAggregate(
          {
            analysisId,
            versionId,
            data: {
              aggregatedAssetsAmount: {
                value: Number(aggregateFinancialAssetsAmount),
                currencyCode: "CZK",
                frequency: AmountFrequency.one_time,
              },
              isUsableForGoals,
            },
          },
          {
            onSuccess: () => {
              invalidateFinancialAssets();
              toast.success(t(Resources.AnalysisDetail.Assets.Financial.Edited));
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error)),
          },
        );

      if (hasAllocatedFinancialAssets && !isUsableForGoals) {
        openConfirmationModal(mutate);
        return;
      }
      mutate();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Financial.Error));
    }
  };

  const onAggregateLiabilityAmountChange = () => {
    try {
      updateLiabilitiesAggregate(
        {
          analysisId,
          versionId,
          data: {
            aggregatedAssetsAmount: { value: Number(aggregateLiabilitiesAmount), currencyCode: "CZK", frequency: AmountFrequency.one_time },
          },
        },
        {
          onSuccess: () => {
            invalidateLiabilities();
            toast.success(t(Resources.AnalysisDetail.Assets.Liabilities.Edited));
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error));
    }
  };

  const { immovableAssetsAmount, movableAssetsAmount, financialAssetsAmount, liabilitiesAmount, unusableAmount } =
    calculateAssetsCombinedValue(assets, immovableAssetItems, movableAssetItems, financialAssetItems, liabilityItems);

  if (isAssetsLoading) {
    return <Loader />;
  }

  return (
    <>
      <Box sx={{ marginBottom: "120px !important" }}>
        <Stack direction={isTablet ? "column" : "row"}>
          <Stack sx={{ flex: 4 }} spacing={Spacing.XXXL}>
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography variant="h3">{t(Resources.AnalysisDetail.TabMenu.Assets)}</Typography>
                <Typography variant="body2" color="secondary">
                  {t(Resources.AnalysisDetail.Assets.Subtitle)}
                </Typography>
              </Box>
            </Box>

            <Stack width={640} spacing={Spacing.XXL} component="form">
              <Stack spacing={Spacing.XL}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Assets.Immovable.Title)}
                </Typography>
                <Box>
                  <BlRadioButtons
                    control={control}
                    disabled={readOnly}
                    name="immovableAssetsEntryType"
                    label={t(Resources.AnalysisDetail.Assets.EntryType)}
                    codeList={[
                      {
                        code: "aggregate",
                        name: t(Resources.AnalysisDetail.Assets.Aggregated),
                      },
                      {
                        code: "individual",
                        name: t(Resources.AnalysisDetail.Assets.Individual),
                      },
                    ]}
                    onChange={(value) => {
                      changeImmovableAmountEntryType(value === "aggregate" ? "aggregate" : "individual");
                    }}
                    errors={errors}
                  />
                  {immovableAssetsEntryType === "individual" && (
                    <>
                      {immovableAssetItems && immovableAssetItems.length > 0 ? (
                        <Grid container spacing={Spacing.L} mb={Spacing.L}>
                          {immovableAssetItems.map((asset, index) => (
                            <Grid key={index.toString()} size={6}>
                              <ImmovableAssetTile
                                onEdit={() => {
                                  setImmovableAssetData(asset);
                                  setImmovableAssetDrawerOpen(true);
                                }}
                                asset={asset}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      ) : null}
                      <Button
                        variant="text"
                        disabled={readOnly}
                        colorCustom={theme.colors.brandPrimary}
                        sx={{ width: "fit-content" }}
                        startAdornment={<AddIcon />}
                        onClick={() => setImmovableAssetDrawerOpen(true)}
                      >
                        {t(Resources.AnalysisDetail.Assets.Immovable.Add)}
                      </Button>
                    </>
                  )}
                </Box>

                {immovableAssetsEntryType === "aggregate" && (
                  <Grid container spacing={Spacing.L}>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="aggregateImmovableAssetsAmount"
                        label={t(Resources.AnalysisDetail.Assets.Immovable.Title)}
                        placeholder={t(Resources.AnalysisDetail.Assets.Immovable.Amount)}
                        errors={errors}
                        labelEndAdornment={<StyledLabelAdornment />}
                        inputEndAdornment={<InputAdornment position="end">{t(Resources.Common.CZKSuffix)}</InputAdornment>}
                        onBlur={() => onAggregateImmovableAssetAmountChange(isImmovableAggregateUsableForGoals)}
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                            radix: ",",
                            mapToRadix: ["."],
                            min: 0,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={6} display="flex" alignItems="center">
                      <Box mt={Spacing.XL}>
                        <BlCheckboxInput
                          control={control}
                          disabled={readOnly}
                          name="isImmovableAggregateUsableForGoals"
                          text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                          errors={errors}
                          onChange={(checked) => {
                            onAggregateImmovableAssetAmountChange(checked);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Stack>

              <Divider />

              <Stack spacing={Spacing.XL}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Assets.Movable.Title)}
                </Typography>
                <Box>
                  <Box>
                    <BlRadioButtons
                      control={control}
                      disabled={readOnly}
                      name="movableAssetsEntryType"
                      label={t(Resources.AnalysisDetail.Assets.EntryType)}
                      codeList={[
                        {
                          code: "aggregate",
                          name: t(Resources.AnalysisDetail.Assets.Aggregated),
                        },
                        {
                          code: "individual",
                          name: t(Resources.AnalysisDetail.Assets.Individual),
                        },
                      ]}
                      onChange={(value) => {
                        changeMovableAmountEntryType(value === "aggregate" ? "aggregate" : "individual");
                      }}
                      errors={errors}
                    />
                    {movableAssetsEntryType === "individual" && (
                      <>
                        {movableAssetItems && movableAssetItems.length > 0 ? (
                          <Grid container spacing={Spacing.L} mb={Spacing.L}>
                            {movableAssetItems.map((asset, index) => (
                              <Grid key={index.toString()} size={6}>
                                <MovableAssetTile
                                  onEdit={() => {
                                    setMovableAssetData(asset);
                                    setMovableAssetDrawerOpen(true);
                                  }}
                                  asset={asset}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ) : null}
                        <Button
                          variant="text"
                          disabled={readOnly}
                          colorCustom={theme.colors.brandPrimary}
                          sx={{ width: "fit-content" }}
                          startAdornment={<AddIcon />}
                          onClick={() => setMovableAssetDrawerOpen(true)}
                        >
                          {t(Resources.AnalysisDetail.Assets.Movable.Add)}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                {movableAssetsEntryType === "aggregate" && (
                  <Grid container spacing={Spacing.L}>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="aggregateMovableAssetsAmount"
                        label={t(Resources.AnalysisDetail.Assets.Movable.Title)}
                        placeholder={t(Resources.AnalysisDetail.Assets.Movable.Amount)}
                        errors={errors}
                        labelEndAdornment={<StyledLabelAdornment />}
                        inputEndAdornment={<InputAdornment position="end">{t(Resources.Common.CZKSuffix)}</InputAdornment>}
                        onBlur={() => onAggregateMovableAssetAmountChange(isMovableAggregateUsableForGoals)}
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                            radix: ",",
                            mapToRadix: ["."],
                            min: 0,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={6} display="flex" alignItems="center">
                      <Box mt={Spacing.XL}>
                        <BlCheckboxInput
                          control={control}
                          disabled={readOnly}
                          name="isMovableAggregateUsableForGoals"
                          text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                          errors={errors}
                          onChange={(checked) => {
                            onAggregateMovableAssetAmountChange(checked);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Stack>

              <Divider />

              <Stack spacing={Spacing.XL}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Assets.Financial.Title)}
                </Typography>
                <Box>
                  <Box>
                    <BlRadioButtons
                      control={control}
                      disabled={readOnly}
                      name="financialAssetsEntryType"
                      label={t(Resources.AnalysisDetail.Assets.EntryType)}
                      codeList={[
                        {
                          code: "aggregate",
                          name: t(Resources.AnalysisDetail.Assets.Aggregated),
                        },
                        {
                          code: "individual",
                          name: t(Resources.AnalysisDetail.Assets.Individual),
                        },
                      ]}
                      onChange={(value) => {
                        changeFinancialAmountEntryType(value === "aggregate" ? "aggregate" : "individual");
                      }}
                      errors={errors}
                    />
                    {financialAssetsEntryType === "individual" && (
                      <>
                        {financialAssetItems && financialAssetItems.length > 0 ? (
                          <Grid container spacing={Spacing.L} mb={Spacing.L}>
                            {financialAssetItems.map((asset, index) => (
                              <Grid key={index.toString()} size={6}>
                                <FinancialAssetTile
                                  onEdit={() => {
                                    setFinancialAssetData(asset);
                                    setFinancialAssetDrawerOpen(true);
                                  }}
                                  asset={asset}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ) : null}
                        <Button
                          variant="text"
                          disabled={readOnly}
                          colorCustom={theme.colors.brandPrimary}
                          sx={{ width: "fit-content" }}
                          startAdornment={<AddIcon />}
                          onClick={() => setFinancialAssetDrawerOpen(true)}
                        >
                          {t(Resources.AnalysisDetail.Assets.Financial.Add)}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                {financialAssetsEntryType === "aggregate" && (
                  <Grid container spacing={Spacing.L}>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="aggregateFinancialAssetsAmount"
                        label={t(Resources.AnalysisDetail.Assets.Financial.Title)}
                        placeholder={t(Resources.AnalysisDetail.Assets.Financial.Amount)}
                        errors={errors}
                        labelEndAdornment={<StyledLabelAdornment />}
                        inputEndAdornment={<InputAdornment position="end">{t(Resources.Common.CZKSuffix)}</InputAdornment>}
                        onBlur={() => onAggregateFinancialAssetAmountChange(isFinancialAggregateUsableForGoals)}
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                            radix: ",",
                            mapToRadix: ["."],
                            min: 0,
                          },
                        ]}
                      />
                    </Grid>
                    <Grid size={6} display="flex" alignItems="center">
                      <Box mt={Spacing.XL}>
                        <BlCheckboxInput
                          control={control}
                          disabled={readOnly}
                          name="isFinancialAggregateUsableForGoals"
                          text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                          errors={errors}
                          onChange={(checked) => {
                            onAggregateFinancialAssetAmountChange(checked);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Stack>

              <Divider />

              <Stack spacing={Spacing.XL}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t(Resources.AnalysisDetail.Assets.Liabilities.Title)}
                </Typography>
                <Box>
                  <Box>
                    <BlRadioButtons
                      control={control}
                      disabled={readOnly}
                      name="liabilitiesEntryType"
                      label={t(Resources.AnalysisDetail.Assets.EntryType)}
                      codeList={[
                        {
                          code: "aggregate",
                          name: t(Resources.AnalysisDetail.Assets.Aggregated),
                        },
                        {
                          code: "individual",
                          name: t(Resources.AnalysisDetail.Assets.Individual),
                        },
                      ]}
                      onChange={(value) => {
                        const mode: EntryMode = value === "aggregate" ? "aggregate" : "individual";
                        setLiabilitiesEntryMode(
                          { analysisId, versionId, mode },
                          {
                            onSuccess: invalidateLiabilities,
                            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Liabilities.Error)),
                          },
                        );
                      }}
                      errors={errors}
                    />
                    {liabilitiesEntryType === "individual" && (
                      <>
                        {liabilityItems && liabilityItems.length > 0 ? (
                          <Grid container spacing={Spacing.L} mb={Spacing.L}>
                            {liabilityItems.map((liability, index) => (
                              <Grid key={index.toString()} size={6}>
                                <LiabilityTile
                                  onEdit={() => {
                                    setLiabilityData(liability);
                                    setLiabilityDrawerOpen(true);
                                  }}
                                  liability={liability}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        ) : null}
                        <Button
                          variant="text"
                          disabled={readOnly}
                          colorCustom={theme.colors.brandPrimary}
                          sx={{ width: "fit-content" }}
                          startAdornment={<AddIcon />}
                          onClick={() => setLiabilityDrawerOpen(true)}
                        >
                          {t(Resources.AnalysisDetail.Assets.Liabilities.Add)}
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                {liabilitiesEntryType === "aggregate" && (
                  <Grid container spacing={Spacing.L}>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name="aggregateLiabilitiesAmount"
                        label={t(Resources.AnalysisDetail.Assets.Liabilities.Title)}
                        placeholder={t(Resources.AnalysisDetail.Assets.Liabilities.Amount)}
                        errors={errors}
                        labelEndAdornment={<StyledLabelAdornment />}
                        inputEndAdornment={<InputAdornment position="end">{t(Resources.Common.CZKSuffix)}</InputAdornment>}
                        onBlur={onAggregateLiabilityAmountChange}
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                            radix: ",",
                            mapToRadix: ["."],
                            min: 0,
                          },
                        ]}
                      />
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Stack>
          </Stack>
          <Divider orientation={isTablet ? "horizontal" : "vertical"} flexItem sx={{ marginTop: isTablet ? "48px" : "-24px !important" }} />{" "}
          {/* !important here because of parent spacing */}
          <Stack spacing={Spacing.L} sx={{ flex: 2 }}>
            <AssetSummary
              immovableAssetsAmount={immovableAssetsAmount}
              movableAssetsAmount={movableAssetsAmount}
              financialAssetsAmount={financialAssetsAmount}
              liabilitiesAmount={liabilitiesAmount}
              unusableAmount={unusableAmount}
              isTablet={isTablet}
            />
          </Stack>
        </Stack>

        <ImmovableAssetDrawer
          immovableAsset={immovableAssetData}
          onClose={() => {
            setImmovableAssetDrawerOpen(false);
            setImmovableAssetData(undefined);
          }}
          open={immovableAssetDrawerOpen}
        />
        <MovableAssetDrawer
          movableAsset={movableAssetData}
          onClose={() => {
            setMovableAssetDrawerOpen(false);
            setMovableAssetData(undefined);
          }}
          open={movableAssetDrawerOpen}
        />
        <FinancialAssetDrawer
          financialAsset={financialAssetData}
          onClose={() => {
            setFinancialAssetDrawerOpen(false);
            setFinancialAssetData(undefined);
          }}
          open={financialAssetDrawerOpen}
        />
        <LiabilityDrawer
          liability={liabilityData}
          onClose={() => {
            setLiabilityDrawerOpen(false);
            setLiabilityData(undefined);
          }}
          open={liabilityDrawerOpen}
        />
      </Box>
      <Modal open={confirmationModalOpen} onClose={handleConfirmationCancel}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: 0,
          }}
        >
          <StyledWrapper sx={{ boxShadow: 3 }}>
            <Stack spacing={Spacing.XXL}>
              <Box>
                <Typography variant="h2">{t(Resources.AnalysisDetail.Assets.ModelingModal.Title)}</Typography>
              </Box>

              <Typography variant="body1">{t(Resources.AnalysisDetail.Assets.ModelingModal.Body)}</Typography>
              <Box display="flex" justifyContent="space-between">
                <Button color="secondary" onClick={handleConfirmationCancel}>
                  {t(Resources.Common.Close)}
                </Button>

                <Button disabled={readOnly} onClick={handleConfirmationSubmit}>
                  {t(Resources.Common.Yes)}
                </Button>
              </Box>
            </Stack>
          </StyledWrapper>
        </Box>
      </Modal>
    </>
  );
};
